import { useEffect, useMemo, useState } from "react"
import { Box, Button, Typography } from "@mui/material"
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined"
import { observer } from "mobx-react-lite"
import { rootStore } from "stores/root-store"
import { ContentWrapper, MainComponent } from "components/common-styles"
import { TopbarPortal, TOPBAR_TITLE_SLOT_ID } from "components/layout/topbar-portal"
import { DEFAULT_PERIOD, deriveVolumeView } from "pages/analytics/volume/volume-derive"
import { PeriodFilter, downloadCsv } from "components/analytics/page-shared"
import { InboundChart, InboundReturnsTable, KpiTile, SplitKpiTile } from "components/analytics/widgets"
import { analyticsBusiness } from "businesses/home-business"
import { trackEvent } from "utils/analytics"

const C = { text: "#12131A", muted: "#65748B", surfaceMuted: "#F9FAFC" }

const fmt = n => (typeof n === "number" ? n.toLocaleString() : n)
const gridSx = { display: "grid", gap: 1.5 }
const EMPTY_VOLUME_PAYLOAD = { info: {}, outbound: [], inbound: [], dwh_last_date: null, dwh_unavailable: true }

const InboundAnalytics = observer(() => {
  const [period, setPeriod] = useState(DEFAULT_PERIOD)
  const [payload, setPayload] = useState(EMPTY_VOLUME_PAYLOAD)

  useEffect(() => {
    rootStore.setScreenTitle("")
  }, [])

  const company = rootStore.company

  useEffect(() => {
    let cancelled = false

    const loadAnalytics = async () => {
      const next = await analyticsBusiness.getInboundVolumePayload(company)
      if (!cancelled) setPayload(next)
    }

    loadAnalytics()
    return () => {
      cancelled = true
    }
  }, [company])

  useEffect(() => {
    trackEvent("analytics_view", { report: "inbound", period: typeof period === "string" ? period : "custom" })
  }, [period, company])

  const view = useMemo(() => deriveVolumeView(payload, period), [payload, period])
  const minSelectableYear = useMemo(() => {
    const years = (payload?.inbound || []).map(row => Number(String(row?.year_month || "").slice(0, 4))).filter(Number.isFinite)
    return years.length ? Math.min(...years) : null
  }, [payload])
  const ik = view.inboundKpis

  const exportInbound = () =>
    downloadCsv(
      `inbound-${view.companyCode || "customer"}.csv`,
      [
        { key: "label", label: "Month" },
        { key: "lcl", label: "LCL shipments" },
        { key: "lclUnits", label: "LCL units" },
        { key: "fcl20", label: "20ft FCL shipments" },
        { key: "fcl20Units", label: "20ft FCL units" },
        { key: "fcl40", label: "40ft FCL shipments" },
        { key: "fcl40Units", label: "40ft FCL units" },
        { key: "returnsUnits", label: "Returns (units)" },
      ],
      view.inboundReturnsDetail,
      "analytics_inbound",
    )

  return (
    <MainComponent component="main" sx={{ bgcolor: C.surfaceMuted, minHeight: "100%", display: "flex" }}>
      <ContentWrapper maxWidth={false} sx={{ py: 2 }}>
        <TopbarPortal slot={TOPBAR_TITLE_SLOT_ID}>
          <Box sx={{ display: "flex", alignItems: "baseline", gap: 1 }}>
            <Typography sx={{ fontSize: 17, fontWeight: 800, color: C.text, letterSpacing: -0.3, whiteSpace: "nowrap" }}>Inbound analysis</Typography>
            <Typography variant="caption" sx={{ color: C.muted, whiteSpace: "nowrap", display: { xs: "none", xl: "block" } }}>
              {view.period.label} · {view.name}
            </Typography>
          </Box>
        </TopbarPortal>

        <TopbarPortal>
          <PeriodFilter period={period} onChange={setPeriod} minYear={minSelectableYear} />
          <Button
            variant="outlined"
            size="small"
            startIcon={<FileDownloadOutlinedIcon />}
            onClick={exportInbound}
            sx={{ borderColor: "#D7DBE5", color: C.text, fontWeight: 700, "&:hover": { borderColor: C.text, bgcolor: "transparent" } }}
          >
            Inbound CSV
          </Button>
        </TopbarPortal>

        {/* KPI tiles — split cells (shipments | units); Returns stays single and zero-visible */}
        <Box sx={{ ...gridSx, gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "repeat(4, 1fr)" }, mb: 1.5 }}>
          <SplitKpiTile label="LCL" shipments={ik.lclInbound.shipments} units={ik.lclInbound.units} subtext="Closed receipts · physically received" />
          <SplitKpiTile label="20ft FCL" shipments={ik.fcl20.shipments} units={ik.fcl20.units} subtext="Closed receipts · physically received" />
          <SplitKpiTile label="40ft FCL" shipments={ik.fcl40.shipments} units={ik.fcl40.units} subtext="Closed receipts · physically received" />
          <KpiTile label="Return units/pieces" value={ik.returns.units} subtext={`${fmt(ik.returns.shipments)} shipments · physically received`} />
        </Box>

        <Box sx={{ ...gridSx, gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" } }}>
          <InboundChart data={view} />
          <InboundReturnsTable data={view} />
        </Box>
      </ContentWrapper>
    </MainComponent>
  )
})

export default InboundAnalytics
